import { motion } from "framer-motion";
import { Truck, MapPin, Shield, Clock, Package, BarChart3 } from "lucide-react";
import { IMAGES } from "../lib/media";

const LOGISTICS_CARDS = [
  {
    icon: Truck,
    title: "Last-Mile Delivery",
    desc: "Precision routing for heavy-load logistics across urban and industrial zones.",
    span: "col-span-1 row-span-1",
    accent: "ib-blue",
  },
  {
    icon: MapPin,
    title: "Real-Time Tracking",
    desc: "GPS-enabled fleet monitoring with live ETA updates and route optimization.",
    span: "col-span-1 row-span-2",
    accent: "ib-lime",
    featured: true,
  },
  {
    icon: Shield,
    title: "Supply Chain Transparency",
    desc: "End-to-end visibility from mill to warehouse with tamper-proof documentation.",
    span: "col-span-1 row-span-1",
    accent: "ib-blue",
  },
  {
    icon: Clock,
    title: "Delivery Windows",
    desc: "Scheduled delivery slots with 98.5% on-time performance guarantee.",
    span: "col-span-1 row-span-1",
    accent: "ib-blue",
  },
  {
    icon: Package,
    title: "Inventory Management",
    desc: "Automated stock monitoring with predictive reorder alerts and warehouse integration.",
    span: "col-span-1 row-span-1",
    accent: "ib-blue",
  },
  {
    icon: BarChart3,
    title: "Analytics Dashboard",
    desc: "Comprehensive logistics KPIs, cost analysis, and performance benchmarking.",
    span: "col-span-2 row-span-1",
    accent: "ib-lime",
  },
];

export function IronLogistics() {
  return (
    <section id="logistics" data-testid="logistics-section" className="py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 items-end"
        >
          <div>
          <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">
            ironLogistics
          </span>
          <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 text-ib-text">
            Industrial-Grade <span className="text-ib-blue">Logistics</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-ib-text-secondary mt-4 max-w-xl">
            Built for heavy tonnage. Optimized for speed. Complete supply chain
            transparency from procurement to delivery.
          </p>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-ib-steel/20 h-52" data-testid="logistics-image">
            <img src={IMAGES.truck} alt="Truck loaded with steel coils" loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ib-bg/80 to-transparent" />
            <span className="absolute bottom-3 left-4 font-jetbrains text-[10px] text-ib-lime tracking-widest uppercase">Coil to site · Pan-India</span>
          </div>
        </motion.div>

        {/* Bento Grid - Control Room mode */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {LOGISTICS_CARDS.map((card) => {
            const Icon = card.icon;
            const cardIdx = LOGISTICS_CARDS.indexOf(card);
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: cardIdx * 0.08 }}
                className={`bento-card rounded-lg border border-ib-steel/20 bg-ib-surface/50 p-6 ${card.span} ${
                  card.featured ? "flex flex-col justify-between" : ""
                }`}
                data-testid={`logistics-card-${cardIdx}`}
              >
                <div>
                  <div className={`inline-flex p-2.5 rounded-lg mb-4 ${
                    card.accent === "ib-lime" ? "bg-ib-lime/10" : "bg-ib-blue/10"
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      card.accent === "ib-lime" ? "text-ib-lime" : "text-ib-blue"
                    }`} />
                  </div>
                  <h3 className="font-outfit font-bold text-lg text-ib-text mb-2">
                    {card.title}
                  </h3>
                  <p className="font-manrope text-sm text-ib-text-secondary leading-relaxed">
                    {card.desc}
                  </p>
                </div>

                {card.featured && (
                  <div className="mt-6 p-3 rounded-md bg-ib-bg/50 border border-ib-steel/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-jetbrains text-[10px] text-ib-text-muted">SHIPMENT #IB-4892</span>
                      <span className="font-jetbrains text-[10px] text-ib-lime px-2 py-0.5 bg-ib-lime/10 rounded">IN TRANSIT</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-ib-steel/20 overflow-hidden">
                      <div className="h-full w-[72%] bg-gradient-to-r from-ib-blue to-ib-lime rounded-full" />
                    </div>
                    <div className="flex justify-between mt-1.5">
                      <span className="font-jetbrains text-[9px] text-ib-text-muted">Mumbai</span>
                      <span className="font-jetbrains text-[9px] text-ib-text-muted">72%</span>
                      <span className="font-jetbrains text-[9px] text-ib-text-muted">Rotterdam</span>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
